<template>
  <div
    :class="`propeller-product-card group relative flex h-full overflow-hidden rounded-[var(--radius-container)] border border-border bg-card shadow-sm transition-all duration-200 hover:shadow-md hover:border-secondary/20 ${
      isRow() ? 'flex-row flex-wrap md:flex-nowrap items-center' : 'flex-col'
    } ${className || ''}`"
    :data-layout="isRow() ? 'row' : 'grid'"
  >
    <template v-if="showImage !== false">
      <!-- Injected imageComponent takes over the full image area
           (no auto-rendered badges/favorite around it). -->
      <component
        v-if="props.imageComponent"
        :is="ImageImpl"
        :product="product"
        :language="language"
        :image-search-filters="configuration?.imageSearchFiltersGrid"
        :image-variant-filters="configuration?.imageVariantFiltersMedium"
        :class="`propeller-product-card__media relative overflow-hidden bg-surface-hover ${
          isRow()
            ? 'w-20 h-20 flex-shrink-0 p-2'
            : 'aspect-[4/3] sm:aspect-square p-2 sm:p-4'
        }`"
      />
      <!-- Default image area; consumer can override via #image slot.
           Slot default still renders the badges/favorite injection/slot blocks. -->
      <slot
        v-else
        name="image"
        :product="product"
        :language="language"
        :imageUrl="getProductImageUrl()"
        :imageSearchFilters="configuration?.imageSearchFiltersGrid"
        :imageVariantFilters="configuration?.imageVariantFiltersMedium"
        :onNavigate="handleNavigate"
      >
        <div
          :class="`propeller-product-card__media relative overflow-hidden bg-surface-hover ${
            isRow()
              ? 'w-20 h-20 flex-shrink-0 p-2'
              : 'aspect-[4/3] sm:aspect-square p-2 sm:p-4'
          }`"
        >
          <span
            class="block h-full w-full cursor-pointer"
            @click="handleNavigate()"
          >
            <template v-if="!!getProductImageUrl()">
              <img
                class="propeller-product-card__image h-full w-full object-contain transition-transform duration-300 group-hover:scale-105"
                :src="getProductImageUrl()"
                :alt="getProductName()"
              />
            </template>

            <template v-if="!getProductImageUrl()">
              <div
                class="propeller-product-card__image-placeholder flex h-full w-full items-center justify-center text-foreground-subtle"
              >
                <svg
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  class="h-16 w-16"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    :strokeWidth="1"
                  ></path>
                </svg>
              </div>
            </template>
          </span>
          <!-- Injected badgesComponent takes precedence; otherwise #badges slot
               (default content is the inline badges block). -->
          <component
            v-if="props.badgesComponent"
            :is="BadgesImpl"
            :product="product"
            :labels="labels"
          />
          <slot
            v-else-if="
              !!imageLabels &&
              imageLabels.length > 0 &&
              computedImageLabels().length > 0
            "
            name="badges"
            :product="product"
            :imageLabels="computedImageLabels()"
            :labels="labels"
          >
            <div
              class="propeller-product-card__badges pointer-events-none absolute left-2 top-2 flex flex-col gap-1"
            >
              <template
                :key="index"
                v-for="(label, index) in computedImageLabels()"
              >
                <span
                  class="propeller-product-card__badge inline-block rounded bg-secondary px-2 py-0.5 text-xs font-medium text-primary-foreground shadow-sm"
                  >{{ label }}</span
                >
              </template>
            </div>
          </slot>

          <!-- Injected favoriteComponent takes precedence; otherwise #favorite slot
               (default content is the inline heart button). -->
          <component
            v-if="props.favoriteComponent && enableAddFavorite"
            :is="FavoriteImpl"
            :product="product"
            :user="user"
            :on-toggle-favorite="onToggleFavorite"
            :labels="labels"
          />
          <slot
            v-else-if="enableAddFavorite"
            name="favorite"
            :product="product"
            :isFavorite="isFavorite"
            :toggle="handleToggleFavorite"
            :labels="labels"
          >
            <button
              type="button"
              @click="async (e) => handleToggleFavorite(e)"
              :aria-label="
                isFavorite
                  ? getLabel('removeFromFavorites', 'Remove from favourites')
                  : getLabel('addToFavorites', 'Add to favourites')
              "
              :data-favorite="isFavorite ? 'true' : 'false'"
              :class="`propeller-product-card__favorite-btn absolute right-2 top-2 rounded-full border bg-card p-1.5 shadow-sm transition-colors ${
                isFavorite
                  ? 'border-destructive text-destructive'
                  : 'border-border-subtle text-foreground-subtle hover:text-destructive'
              }`"
            >
              <svg
                stroke="currentColor"
                viewBox="0 0 24 24"
                class="h-4 w-4"
                :fill="isFavorite ? 'currentColor' : 'none'"
                :strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                ></path>
              </svg>
            </button>
          </slot>
        </div>
      </slot>
    </template>

    <template v-if="isRow()">
      <div
        class="propeller-product-card__body flex flex-1 flex-row items-center gap-4 px-4 py-2 min-w-0"
      >
        <div class="flex flex-col gap-0.5 flex-1 min-w-0">
          <slot
            v-if="showSku !== false && !!getProductSku()"
            name="sku"
            :product="product"
            :sku="getProductSku()"
          >
            <div
              class="propeller-product-card__sku font-mono text-xs text-foreground-subtle"
            >
              {{ getProductSku() }}
            </div>
          </slot>

          <slot
            v-if="showName !== false"
            name="name"
            :product="product"
            :productUrl="getProductUrl()"
            :handleProductClick="handleProductClick"
            :linkable="true"
            :name="getProductName()"
            :onNavigate="handleNavigate"
          >
            <span
              class="propeller-product-card__title text-sm font-medium leading-tight text-foreground transition-colors hover:text-primary line-clamp-1 cursor-pointer"
              @click="handleNavigate()"
              >{{ getProductName() }}</span
            >
          </slot>

          <slot name="belowName" :product="product">
            <div
              v-if="BelowNameImpl"
              class="propeller-product-card__below-name"
            >
              <component :is="BelowNameImpl" :product="product" />
            </div>
          </slot>

          <slot
            v-if="
              !!textLabels &&
              textLabels.length > 0 &&
              computedTextLabels().length > 0
            "
            name="textLabels"
            :product="product"
            :values="computedTextLabels()"
          >
            <div class="propeller-product-card__labels flex flex-col gap-0.5">
              <template
                :key="index"
                v-for="(item, index) in computedTextLabels()"
              >
                <div
                  class="propeller-product-card__label text-xs text-muted-foreground"
                >
                  {{ item.value }}
                </div>
              </template>
            </div>
          </slot>

          <slot
            v-if="showManufacturer && !!getProductManufacturer()"
            name="manufacturer"
            :product="product"
            :manufacturer="getProductManufacturer()"
          >
            <div
              class="propeller-product-card__manufacturer text-xs text-muted-foreground"
            >
              {{ getProductManufacturer() }}
            </div>
          </slot>

          <slot
            v-if="showShortDescription && !!getProductShortDescription()"
            name="shortDescription"
            :product="product"
            :text="getProductShortDescription()"
          >
            <p
              class="propeller-product-card__description line-clamp-2 text-xs text-muted-foreground"
            >
              {{ getProductShortDescription() }}
            </p>
          </slot>
        </div>
      </div>
      <div
        class="propeller-product-card__footer w-full md:w-auto flex flex-col gap-2 md:flex-row md:items-center md:gap-3 px-4 py-2 md:py-0 border-t md:border-t-0 border-border-subtle"
      >
        <div
          class="propeller-product-card__footer-meta flex items-center justify-between gap-3 md:contents"
        >
        <slot
          v-if="showStock && !!props.product.inventory"
          name="stock"
          :product="product"
          :inventory="product.inventory"
          :showAvailability="false"
          :labels="props.stockLabels"
        >
          <component
            v-if="props.stockComponent"
            :is="StockImpl"
            :inventory="props.product.inventory"
            :show-availability="false"
            :show-stock="true"
            :labels="props.stockLabels"
          />
          <ItemStock
            v-else
            :inventory="props.product.inventory"
            :showAvailability="false"
            :showStock="true"
            :labels="props.stockLabels"
          ></ItemStock>
        </slot>

        <slot
          v-if="showPrice !== false && !!product?.price"
          name="price"
          :product="product"
          :price="product.price"
          :includeTax="resolvedIncludeTax"
          :currency="currency"
          :labels="props.priceLabels"
        >
          <div class="propeller-product-card__price">
            <component
              v-if="props.priceComponent"
              :is="PriceImpl"
              :price="product.price"
              :include-tax="
                resolvedIncludeTax
              "
              :currency="currency"
              :labels="props.priceLabels"
            />
            <ProductPriceDisplay
              v-else
              :price="product.price"
              :includeTax="
                resolvedIncludeTax
              "
              priceSize="text-sm"
              :labels="props.priceLabels"
              :portalMode="props.portalMode ?? infra.portalMode"
              :user="props.user ?? infra.user"
              :showLoginPrompt="false"
            />
          </div>
        </slot>
        </div>

        <div class="propeller-product-card__cta w-full md:w-auto md:flex-shrink-0 md:ml-auto">
          <slot
            name="addToCart"
            :product="product"
            :cartId="props.cartId"
            :labels="props.addToCartLabels"
          >
            <!-- Before the injected component so a host control is gated too. -->
            <LoginToOrderButton
              v-if="contentHidden"
              :labels="props.addToCartLabels"
              :on-login-click="props.onLoginClick"
            />
            <component
              v-else-if="props.addToCartComponent"
              :is="AddToCartImpl"
              :product="props.product"
              :cart-id="props.cartId"
              :allow-incr-decr="props.allowIncrDecr"
              :show-modal="props.showModal"
              :enable-stock-validation="props.enableStockValidation"
              :on-add-to-cart="props.onAddToCart"
              :after-add-to-cart="props.afterAddToCart"
              :on-proceed-to-checkout="props.onProceedToCheckout"
              :on-request-quote-click="props.onRequestQuoteClick"
              :create-cart="props.createCart"
              :on-cart-created="props.onCartCreated"
              :include-tax="props.includeTax"
              :labels="props.addToCartLabels"
            />
            <AddToCart
              v-else
              :graphqlClient="props.graphqlClient"
              :user="props.user"
              :product="props.product"
              :cartId="props.cartId"
              :configuration="props.configuration"
              :childItems="props.childItems"
              :notes="props.notes"
              :price="props.price"
              :createCart="props.createCart"
              :onCartCreated="props.onCartCreated"
              :onAddToCart="props.onAddToCart"
              :afterAddToCart="props.afterAddToCart"
              :showModal="props.showModal"
              :allowIncrDecr="props.allowIncrDecr"
              :enableStockValidation="props.enableStockValidation"
              :language="props.language"
              :onProceedToCheckout="props.onProceedToCheckout"
              :onRequestQuoteClick="props.onRequestQuoteClick"
              :labels="props.addToCartLabels"
              :companyId="props.companyId"
            ></AddToCart>
          </slot>
        </div>
      </div>
    </template>

    <template v-if="!isRow()">
      <div
        class="propeller-product-card__body flex flex-1 flex-col gap-1.5 p-3 sm:gap-2 sm:p-4"
      >
        <slot
          v-if="showSku !== false && !!getProductSku()"
          name="sku"
          :product="product"
          :sku="getProductSku()"
        >
          <div
            class="propeller-product-card__sku font-mono text-xs text-foreground-subtle"
          >
            {{ getProductSku() }}
          </div>
        </slot>

        <slot
          v-if="showName !== false"
          name="name"
          :product="product"
          :productUrl="getProductUrl()"
          :handleProductClick="handleProductClick"
          :linkable="true"
          :name="getProductName()"
          :onNavigate="handleNavigate"
        >
          <span
            class="propeller-product-card__title text-sm font-medium leading-tight text-foreground transition-colors hover:text-primary line-clamp-2 cursor-pointer"
            @click="handleNavigate()"
            >{{ getProductName() }}</span
          >
        </slot>

        <slot name="belowName" :product="product">
          <div
            v-if="BelowNameImpl"
            class="propeller-product-card__below-name"
          >
            <component :is="BelowNameImpl" :product="product" />
          </div>
        </slot>

        <slot
          v-if="
            !!textLabels &&
            textLabels.length > 0 &&
            computedTextLabels().length > 0
          "
          name="textLabels"
          :product="product"
          :values="computedTextLabels()"
        >
          <div class="propeller-product-card__labels flex flex-col gap-0.5">
            <template
              :key="index"
              v-for="(item, index) in computedTextLabels()"
            >
              <div
                class="propeller-product-card__label text-xs text-muted-foreground"
              >
                {{ item.value }}
              </div>
            </template>
          </div>
        </slot>

        <div
          v-if="showStock && !!props.product.inventory"
          class="hidden md:block"
        >
          <slot
            name="stock"
            :product="product"
            :inventory="product.inventory"
            :showAvailability="props.showAvailability !== false"
            :labels="props.stockLabels"
          >
            <component
              v-if="props.stockComponent"
              :is="StockImpl"
              :inventory="props.product.inventory"
              :show-availability="props.showAvailability !== false"
              :show-stock="true"
              :labels="props.stockLabels"
            />
            <ItemStock
              v-else
              :inventory="props.product.inventory"
              :showAvailability="props.showAvailability !== false"
              :showStock="true"
              :labels="props.stockLabels"
            ></ItemStock>
          </slot>
        </div>

        <slot
          v-if="showManufacturer && !!getProductManufacturer()"
          name="manufacturer"
          :product="product"
          :manufacturer="getProductManufacturer()"
        >
          <div
            class="propeller-product-card__manufacturer text-xs text-muted-foreground"
          >
            {{ getProductManufacturer() }}
          </div>
        </slot>

        <slot
          v-if="showShortDescription && !!getProductShortDescription()"
          name="shortDescription"
          :product="product"
          :text="getProductShortDescription()"
        >
          <p
            class="propeller-product-card__description line-clamp-2 text-xs text-muted-foreground"
          >
            {{ getProductShortDescription() }}
          </p>
        </slot>

        <div
          v-if="showPrice !== false && !!product?.price"
          class="mt-auto hidden md:block"
        >
          <slot
            name="price"
            :product="product"
            :price="product.price"
            :includeTax="resolvedIncludeTax"
            :currency="currency"
            :labels="props.priceLabels"
          >
            <div class="propeller-product-card__price pt-1">
              <component
                v-if="props.priceComponent"
                :is="PriceImpl"
                :price="product.price"
                :include-tax="
                  resolvedIncludeTax
                "
                :currency="currency"
                :labels="props.priceLabels"
              />
              <ProductPriceDisplay
                v-else
                :price="product.price"
                :includeTax="
                  resolvedIncludeTax
                "
                priceSize="text-base sm:text-lg"
                :labels="props.priceLabels"
                :portalMode="props.portalMode ?? infra.portalMode"
                :user="props.user ?? infra.user"
                :showLoginPrompt="false"
              />
            </div>
          </slot>
        </div>
      </div>

      <div
        v-if="(showStock && !!props.product.inventory) || (showPrice !== false && !!product?.price)"
        class="propeller-product-card__footer-meta flex flex-wrap items-center justify-between gap-x-2 gap-y-1 px-3 pt-1 sm:px-4 md:hidden"
      >
        <slot
          v-if="showStock && !!props.product.inventory"
          name="stock"
          :product="product"
          :inventory="product.inventory"
          :showAvailability="props.showAvailability !== false"
          :labels="props.stockLabels"
        >
          <component
            v-if="props.stockComponent"
            :is="StockImpl"
            :inventory="props.product.inventory"
            :show-availability="props.showAvailability !== false"
            :show-stock="true"
            :labels="props.stockLabels"
          />
          <ItemStock
            v-else
            :inventory="props.product.inventory"
            :showAvailability="props.showAvailability !== false"
            :showStock="true"
            :labels="props.stockLabels"
          ></ItemStock>
        </slot>

        <slot
          v-if="showPrice !== false && !!product?.price"
          name="price"
          :product="product"
          :price="product.price"
          :includeTax="resolvedIncludeTax"
          :currency="currency"
          :labels="props.priceLabels"
        >
          <div class="propeller-product-card__price min-w-0 text-right">
            <component
              v-if="props.priceComponent"
              :is="PriceImpl"
              :price="product.price"
              :include-tax="resolvedIncludeTax"
              :currency="currency"
              :labels="props.priceLabels"
            />
            <ProductPriceDisplay
              v-else
              :price="product.price"
              :includeTax="resolvedIncludeTax"
              priceSize="text-base"
              :labels="props.priceLabels"
              :portalMode="props.portalMode ?? infra.portalMode"
              :user="props.user ?? infra.user"
              :showLoginPrompt="false"
            />
          </div>
        </slot>
      </div>

      <template v-if="showCta">
        <div class="propeller-product-card__cta px-3 pb-3 pt-2 sm:px-4 sm:pb-4">
          <slot
            name="addToCart"
            :product="product"
            :cartId="props.cartId"
            :labels="props.addToCartLabels"
          >
            <!-- Before the injected component so a host control is gated too. -->
            <LoginToOrderButton
              v-if="contentHidden"
              :labels="props.addToCartLabels"
              :on-login-click="props.onLoginClick"
            />
            <component
              v-else-if="props.addToCartComponent"
              :is="AddToCartImpl"
              :product="props.product"
              :cart-id="props.cartId"
              :allow-incr-decr="props.allowIncrDecr"
              :show-modal="props.showModal"
              :enable-stock-validation="props.enableStockValidation"
              :on-add-to-cart="props.onAddToCart"
              :after-add-to-cart="props.afterAddToCart"
              :on-proceed-to-checkout="props.onProceedToCheckout"
              :on-request-quote-click="props.onRequestQuoteClick"
              :create-cart="props.createCart"
              :on-cart-created="props.onCartCreated"
              :include-tax="props.includeTax"
              :labels="props.addToCartLabels"
            />
            <AddToCart
              v-else
              :graphqlClient="props.graphqlClient"
              :user="props.user"
              :product="props.product"
              :cartId="props.cartId"
              :configuration="props.configuration"
              :childItems="props.childItems"
              :notes="props.notes"
              :price="props.price"
              :createCart="props.createCart"
              :onCartCreated="props.onCartCreated"
              :onAddToCart="props.onAddToCart"
              :afterAddToCart="props.afterAddToCart"
              :showModal="props.showModal"
              :allowIncrDecr="props.allowIncrDecr"
              :enableStockValidation="props.enableStockValidation"
              :language="props.language"
              :onProceedToCheckout="props.onProceedToCheckout"
              :onRequestQuoteClick="props.onRequestQuoteClick"
              :labels="props.addToCartLabels"
              :companyId="props.companyId"
            ></AddToCart>
          </slot>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, type Component } from "vue";
// No router dependency: this is a standalone library component. Consumers
// pass `onProductClick` for SPA navigation; without it we fall back to a
// plain location change so the card still works in any host.

import {
  GraphQLClient,
  Product,
  Contact,
  Customer,
  Cart,
  CartMainItem,
  AttributeResult,
} from "@propeller-commerce/propeller-sdk-v2";
import type { CartChildItemInput } from "@propeller-commerce/propeller-sdk-v2";
import AddToCart from "./AddToCart.vue";
import ItemStock from "./ItemStock.vue";
import ProductPriceDisplay from "./ProductPrice.vue";
import { getLabel as _getLabel } from '@propeller-commerce/propeller-v2-core-ui';
import { localeForLanguage } from '@propeller-commerce/propeller-v2-core-ui';
import {
  getProductImageUrl as _getProductImageUrl,
  getProductSku as _getProductSku,
} from '@propeller-commerce/propeller-v2-core-ui';
import { getLanguageString } from '@propeller-commerce/propeller-v2-core-ui';
import { isContentHidden } from '@propeller-commerce/propeller-v2-core-ui';
import LoginToOrderButton from './LoginToOrderButton.vue';
import { formatPrice as _formatPrice } from '@propeller-commerce/propeller-v2-core-ui';
import { useResolvedProps, type ResolveSpec } from '../composables/vue/useResolvedProps';
import { useInfraProps } from '../composables/vue/useInfraProps';
import DefaultProductPrice from './ProductPrice.vue';
import DefaultItemStock from './ItemStock.vue';
import DefaultAddToCart from './AddToCart.vue';
import DefaultAddToFavorite from './AddToFavorite.vue';
import DefaultProductImage from './defaults/DefaultProductImage.vue';
import DefaultProductBadges from './defaults/DefaultProductBadges.vue';

export interface ProductCardProps {
  // === Core ===

  /** The product object to display */
  product: Product;

  /** Currency symbol to display. Defaults to '€'. */
  currency?: string;

  // === Display toggles ===

  /** Show the product name. Defaults to true. */
  showName?: boolean;

  /** Show the product image. Defaults to true. */
  showImage?: boolean;

  /** Show the product short description. Defaults to false. */
  showShortDescription?: boolean;

  /** Show the product SKU. Defaults to true. */
  showSku?: boolean;

  /** Show the product manufacturer. Defaults to false. */
  showManufacturer?: boolean;

  /**
   * Show the stock / availability widget below the product name.
   * Uses the embedded `ItemStock` component driven by `product.inventory`.
   * Defaults to false.
   */
  showStock?: boolean;

  /**
   * Show only the availability indicator (Available / Not available) inside ItemStock.
   * Only relevant when `showStock` is true.
   * Defaults to true.
   */
  showAvailability?: boolean;

  /**
   * Show the price below the product name.
   * Defaults to true.
   */
  showPrice?: boolean;

  /**
   * Show the AddToCart component.
   * Defaults to true.
   */
  allowAddToCart?: boolean;

  /**
   * Label overrides forwarded to the embedded ItemStock component.
   * Keys: inStock, outOfStock, lowStock, available, notAvailable, pieces
   */
  stockLabels?: Record<string, string>;

  /** Translated labels forwarded to the embedded `<ProductPrice>` display.
   * See `ProductPriceProps.labels` for slugs (inclTax, exclTax, loginToSeePrices). */
  priceLabels?: Record<string, string>;

  // === Attribute labels ===

  /**
   * Attribute codes/names to look up and display as badge overlays on the product image.
   * Each code is resolved against `product.attributes.items[].attributeDescription.code`
   * (or `.name`). Attributes with no matching value are silently omitted.
   * Example: ['new', 'sale']
   */
  imageLabels?: string[];

  /**
   * Attribute codes/names to look up and display as extra text rows below the product name.
   * Resolved the same way as `imageLabels`.
   * Example: ['brand', 'color']
   */
  textLabels?: string[];

  // === UI string overrides ===

  /**
   * Override any UI string.
   * Available keys: addToFavorites, removeFromFavorites
   */
  labels?: Record<string, string>;

  // === Favourites ===

  /** Renders a heart-icon toggle button on the product image. Defaults to false. */
  enableAddFavorite?: boolean;

  /**
   * Called whenever the favourite state is toggled.
   * The second argument indicates the new state: `true` = added, `false` = removed.
   */
  onToggleFavorite?: (product: Product, isFavorite: boolean) => void;

  // === Navigation ===

  /**
   * Called when the product name or image is clicked.
   * When provided, the default `<a>` navigation is prevented so the consumer
   * can use framework-specific routing (e.g. Next.js `router.push`).
   */
  onProductClick?: (product: Product) => void;

  // === Pricing ===

  /**
   * When true, tax-inclusive price (net) is the leading price.
   * When false, tax-exclusive price (gross) is shown.
   * Defaults to false.
   */
  includeTax?: boolean;

  // === Appearance ===

  /** Number of grid columns — when 1 the card renders as a compact horizontal row. */
  columns?: number;

  /** Extra CSS class applied to the root element. */
  className?: string;

  /**
   * URL pattern controlling which segments appear in product links.
   * Tokens: page → 'product', id → productId, slug → slug value.
   * Examples: 'page/id/slug' (default) | 'page/slug' | 'page/id'
   * Defaults to 'page/id/slug' when omitted.
   */
  urlPattern?: string;

  // === AddToCart pass-through props ===

  /** Initialised Propeller SDK GraphQL client (required by embedded AddToCart). Resolved from PropellerProvider when omitted. */
  graphqlClient?: GraphQLClient;

  /** Authenticated user — used for cart creation / lookup. Resolved from PropellerProvider when omitted. */
  user?: Contact | Customer | null;

  /**
   * Portal access mode — `'open'` / `'semi-closed'` / `'closed'`. Resolved from
   * PropellerProvider when omitted. In `'semi-closed'` the card hides price and
   * stock from anonymous visitors and offers a log-in action in place of
   * add-to-cart; signed-in users are unaffected.
   */
  portalMode?: string;

  /**
   * Invoked when an anonymous visitor clicks the log-in action that replaces
   * add-to-cart in a semi-closed portal. The host owns navigation.
   */
  onLoginClick?: () => void;

  /** ID of an existing cart to add items to. */
  cartId?: string;

  /** Config object providing imageSearchFiltersGrid and imageVariantFiltersSmall. */
  configuration?: any;

  /** Cluster ID for configurable products. */
  clusterId?: number;

  /** Product IDs of selected cluster child options. */
  childItems?: number[];

  /** Free-text notes attached to the cart item. */
  notes?: string;

  /** Custom unit price override. Omit to use calculated price. */
  price?: number;

  /**
   * When true and no cartId is available, the embedded AddToCart automatically
   * looks up or creates a cart. Always pair with onCartCreated.
   */
  createCart?: boolean;

  /** Called after a new cart is created internally by AddToCart. */
  onCartCreated?: (cart: Cart) => void;

  /**
   * Fully replaces the internal CartService.addItemToCart call inside AddToCart.
   * Must return a Cart object.
   */
  onAddToCart?: (
    product: Product,
    clusterId?: number,
    quantity?: number,
    childItems?: CartChildItemInput[],
    notes?: string,
    price?: number,
    showModal?: boolean,
  ) => Cart;

  /** Called after every successful add-to-cart. Receives the updated cart and the added item. */
  afterAddToCart?: (cart: Cart, item?: CartMainItem) => void;

  /**
   * When true the embedded AddToCart shows a modal after a successful add
   * instead of the default toast notification. Defaults to false.
   */
  showModal?: boolean;

  /**
   * Renders − and + buttons beside the quantity input in AddToCart.
   * Defaults to true.
   */
  allowIncrDecr?: boolean;

  /** Validate stock before adding to cart. Defaults to false. */
  enableStockValidation?: boolean;

  /** Language code forwarded to CartService operations. Defaults to 'NL'. */
  language?: string;

  /**
   * Active company ID from the company switcher.
   * When provided, overrides the user's default company for cart creation and lookup.
   */
  companyId?: number;

  /** Called when the user clicks "Proceed to checkout" inside the AddToCart modal. */
  onProceedToCheckout?: () => void;

  /** Called when the user clicks "Request a Quote" inside the AddToCart modal. */
  onRequestQuoteClick?: (cart: Cart) => void;

  /** Label overrides for UI strings
   *
   * available labels:
   * - outOfStock
   * - noCartId
   * - errorAdding
   * - addedToCart
   * - modalTitle
   * - quantity
   * - continueShopping
   * - proceedToCheckout
   * - requestQuoteButton
   * - add
   * - adding
   */
  addToCartLabels?: Record<string, string>;

  // ───── Extension API ─────
  // Per-card overrides for sub-components. Precedence:
  // explicit prop > ProductGridConfig context > infra > default.
  priceComponent?: Component;
  stockComponent?: Component;
  addToCartComponent?: Component;
  imageComponent?: Component;
  badgesComponent?: Component;
  favoriteComponent?: Component;
  /**
   * Render arbitrary content directly below the product name (and above the
   * short description / price), in both the grid and row layouts. Receives the
   * product as a prop so hosts can surface extra info — e.g. package
   * descriptions, custom badges — without forking the card. Consumers can also
   * use the `#belowName` scoped slot directly; this prop is the cascadable
   * (grid-wide) equivalent.
   */
  belowNameComponent?: Component;
}
interface ProductCardState {
  isFavorite: boolean;
  getProductName: () => string;
  getProductSku: () => string;
  getProductImageUrl: () => string;
  getProductPrice: () => string;
  getProductUrl: () => string;
  getProductShortDescription: () => string;
  getProductManufacturer: () => string;
  getLabel: (key: string, fallback: string) => string;
  getAttributeValue: (code: string) => string;
  handleProductClick: (e: any) => void;
  handleToggleFavorite: (e: any) => void;
  isRow: () => boolean;
  computedImageLabels: () => string[];
  computedTextLabels: () => {
    name: string;
    value: string;
  }[];
}

const props = withDefaults(defineProps<ProductCardProps>(), {
  showImage: true,
  showName: true,
  showSku: true,
  showPrice: true,
  allowAddToCart: true,
  allowIncrDecr: true,
  showAvailability: true,
  showShortDescription: false,
  showManufacturer: false,
  showStock: false,
  enableAddFavorite: false,
});

// ───── Extension API ─────
// Resolve sub-component slots from explicit props → ProductGrid context.
const RESOLVE_SPEC: ResolveSpec<ProductCardProps> = {
  // NOTE: includeTax is resolved separately (see `infra`/`resolvedIncludeTax`
  // below) because the infra fallback must read injected context at setup —
  // useResolvedProps runs inside a `computed` here, where inject() returns null.
  priceComponent: { grid: 'priceComponent' },
  stockComponent: { grid: 'stockComponent' },
  addToCartComponent: { grid: 'addToCartComponent' },
  imageComponent: { grid: 'imageComponent' },
  badgesComponent: { grid: 'badgesComponent' },
  favoriteComponent: { grid: 'favoriteComponent' },
  belowNameComponent: { grid: 'belowNameComponent' },
};

const resolved = computed(() => useResolvedProps(props, RESOLVE_SPEC));

// Resolve infra ONCE at setup. `inject()` (inside useInfraProps) only works
// during setup — calling it lazily from inside a `computed` getter yields a
// null context, which is why the spec-based `resolved.value.includeTax` could
// not see the provider's VAT flag. `useInfraProps` returns a reactive proxy,
// so reads below still track provider changes (the VAT toggle).
const infra = useInfraProps(props);

// Effective tax-inclusive flag: explicit prop > provider infra (VAT toggle) >
// false. Used by both the price text and the embedded <ProductPrice> so they
// can never disagree.
// Vue coerces an absent `includeTax?: boolean` prop to `false`, so `false` is
// indistinguishable from "not set". Treat only an explicit `true` as a host
// override; otherwise defer to the provider scope (the VAT toggle). ProductGrid
// already resolves the effective flag and passes it down explicitly.
const resolvedIncludeTax = computed<boolean>(() =>
  props.includeTax === true ? true : !!infra.includeTax,
);

// Read through `infra` so call sites need not thread portalMode/user.
const contentHidden = computed<boolean>(() =>
  isContentHidden(
    (props.portalMode ?? infra.portalMode) as string | undefined,
    (props.user ?? infra.user) as Contact | Customer | null | undefined,
infra.isAuthenticated as boolean | undefined,
  ),
);
const showStock = computed<boolean>(() => !!props.showStock && !contentHidden.value);
// Keep the CTA slot when hidden — it carries the log-in action instead.
const showCta = computed<boolean>(
  () => props.allowAddToCart !== false || contentHidden.value,
);

const PriceImpl = computed(() => resolved.value.priceComponent ?? DefaultProductPrice);
const StockImpl = computed(() => resolved.value.stockComponent ?? DefaultItemStock);
const AddToCartImpl = computed(() => resolved.value.addToCartComponent ?? DefaultAddToCart);
const ImageImpl = computed(() => resolved.value.imageComponent ?? DefaultProductImage);
const BadgesImpl = computed(() => resolved.value.badgesComponent ?? DefaultProductBadges);
const FavoriteImpl = computed(() => resolved.value.favoriteComponent ?? DefaultAddToFavorite);
// No default — renders only when a host supplies a belowNameComponent (or uses
// the #belowName slot). `null` so the `v-if="BelowNameImpl"` guard skips it.
const BelowNameImpl = computed(() => resolved.value.belowNameComponent ?? null);

const isFavorite = ref<ProductCardState["isFavorite"]>(false);

function isRow(): ReturnType<ProductCardState["isRow"]> {
  return (props.columns as number) === 1;
}
function getProductName(): ReturnType<ProductCardState["getProductName"]> {
  return getLanguageString(
    (props.product as Product)?.names,
    props.language || "NL",
    "Product",
  );
}
function getProductSku(): ReturnType<ProductCardState["getProductSku"]> {
  return _getProductSku(props.product as Product);
}
function getProductImageUrl(): ReturnType<
  ProductCardState["getProductImageUrl"]
> {
  return _getProductImageUrl(props.product as Product);
}
function getProductPrice(): ReturnType<ProductCardState["getProductPrice"]> {
  if (!props.showPrice) return "";
  const priceObj = (props.product as Product)?.price;
  const useTax: boolean = resolvedIncludeTax.value;
  const value: number | undefined = useTax ? priceObj?.net : priceObj?.gross;
  if (!value && value !== 0) return "";
  return _formatPrice(Number(value), { symbol: props.currency ?? "€", locale: localeForLanguage(props.language) });
}
function getProductUrl(): ReturnType<ProductCardState["getProductUrl"]> {
  return props.configuration?.urls?.getProductUrl(props.product, props.language) ?? "#";
}
function getProductShortDescription(): ReturnType<
  ProductCardState["getProductShortDescription"]
> {
  return getLanguageString(
    (props.product as Product)?.shortDescriptions,
    props.language || "NL",
    "",
  );
}
function getProductManufacturer(): ReturnType<
  ProductCardState["getProductManufacturer"]
> {
  return (props.product as Product)?.manufacturer || "";
}
function getLabel(
  key: string,
  fallback: string,
): ReturnType<ProductCardState["getLabel"]> {
  return _getLabel(props.labels, key, fallback);
}
function getAttributeValue(
  code: string,
): ReturnType<ProductCardState["getAttributeValue"]> {
  const attrs = (props.product as Product)?.attributes?.items || [];
  const found = attrs.find(
    (a: AttributeResult) => a.attributeDescription?.name === code,
  );
  return found?.value?.value || "";
}
function handleNavigate(): void {
  if (props.onProductClick) {
    props.onProductClick(props.product);
  } else if (typeof window !== "undefined") {
    // No router in the package — fall back to a native location change.
    window.location.href = getProductUrl();
  }
}
function handleProductClick(
  e: any,
): ReturnType<ProductCardState["handleProductClick"]> {
  if (props.onProductClick) {
    e.preventDefault();
    props.onProductClick(props.product);
  }
}
function handleToggleFavorite(
  e: any,
): ReturnType<ProductCardState["handleToggleFavorite"]> {
  e.preventDefault();
  e.stopPropagation();
  isFavorite.value = !isFavorite.value;
  if (props.onToggleFavorite) {
    props.onToggleFavorite(props.product, isFavorite.value);
  }
}
function computedImageLabels(): ReturnType<
  ProductCardState["computedImageLabels"]
> {
  if (!props.imageLabels || (props.imageLabels as string[]).length === 0)
    return [];
  const attrs = (props.product as Product)?.attributes?.items || [];
  return (props.imageLabels as string[])
    .map((code: string) => {
      const found = attrs.find(
        (a: AttributeResult) => a.attributeDescription?.name === code,
      );
      return found?.value?.value || "";
    })
    .filter((v: string) => v.length > 0);
}
function computedTextLabels(): ReturnType<
  ProductCardState["computedTextLabels"]
> {
  if (!props.textLabels || (props.textLabels as string[]).length === 0)
    return [];
  const attrs = (props.product as Product)?.attributes?.items || [];
  return (props.textLabels as string[])
    .map((code: string) => {
      const found = attrs.find(
        (a: AttributeResult) => a.attributeDescription?.name === code,
      );
      return {
        name: code,
        value: found?.value?.value || "",
      };
    })
    .filter((item: { name: string; value: string }) => item.value.length > 0);
}
</script>
